import React, { useContext, useMemo, useState } from 'react';
import { OrderInput, OrderUtil } from '../../../../../../types/order';
import { Container } from '../../../../../../types/container';
import { TranslationKey } from '../../../../../../types/translation/TranslationKey';
import { t } from '../../../../../../types/translation/Translator';
import { ContainerContext } from '../../../../../../context/ContainerContext';
import Table, { TableHeader } from '../../../../../components/Table';
import { ProductTransactionSpecifiers } from '../../../../../../types/productTransaction';
import { DropdownSearchSelect } from '../../../../../components/Dropdown';
import Button, { DeleteButton } from '../../../../../../VentoryUI/components/common/Button';
import { ColorStyleType } from '../../../../../../VentoryUI/util/color.util';
import { ProductContext } from '../../../../../../context/ProductContext';
import { ProductMasterDataContext } from '../../../../../../context/ProductMasterDataContext';
import PackageIcon from '../../../../../../VentoryUI/icons/Package/PackagIcon';
import { TextInput } from '../../../../../components/TextInput';
import { cloneDeep } from '@apollo/client/utilities';
import BoxIcon from '../../../../../../VentoryUI/icons/Box/BoxIcon';
import { UnitOfMeasureUtil } from '../../../../../../types/uom';

interface CreateOutboundOrderProductContainerPaneProps {
  order: OrderInput;
  setOrder: (order: OrderInput) => void;
  next?: () => void;
  back?: () => void;
  loading?: boolean;
  setError: (err: string) => void;
  validContainers: Container[];
}

export default function CreateOutboundOrderProductContainerPane({
  order,
  setOrder,
  next,
  back,
  setError,
  loading = false,
  validContainers,
}: CreateOutboundOrderProductContainerPaneProps) {
  const { containers } = useContext(ContainerContext);
  const { products } = useContext(ProductContext);
  const { productMasterData } = useContext(ProductMasterDataContext);

  const [selected, setSelected] = useState<Set<string>>(new Set());

  const orderProducts = useMemo(() => {
    return [...order.products.values()];
  }, [order]);

  const getAllProductsForContainer = (containerId: string) => {
    return [...products.values()]
      .map(item => [...item.values()])
      .flat()
      .filter(p => p.containerId === containerId);
  };

  const containersWithQuantity = useMemo(() => {
    const containerProductQuantity = new Map(
      validContainers.map(container => [container.id, getAllProductsForContainer(container.id).length]),
    );

    const containerIds = new Set(
      [...order.products.values()].filter(p => p.fromContainerId).map(p => p.fromContainerId!),
    );

    return validContainers.filter(
      item => !containerIds.has(item.id) && (containerProductQuantity.get(item.id) || 0) > 0,
    );
  }, [products, containers, order]);

  const headers: TableHeader<ProductTransactionSpecifiers>[] = [
    {
      key: 'number',
      name: t(TranslationKey.productNumber).singular.label,
      text: (item: ProductTransactionSpecifiers) => {
        return (
          <div className='flex content-center'>
            <PackageIcon />
            <p className='pl-2 text-[12px] font-[400] text-gray-900'>
              {productMasterData.get(item.pmdId || '')?.productNumber}
            </p>
          </div>
        );
      },
    },
    {
      key: 'container',
      name: t(TranslationKey.container).singular.label,
      text: (item: ProductTransactionSpecifiers) => {
        return containers.get(item.fromContainerId || '')?.identifier || '';
      },
    },
    {
      key: 'quantity',
      name: t(TranslationKey.quantity).singular.label,
      text: (item: ProductTransactionSpecifiers) => {
        if (item.containerId) return <></>;
        return (
          <div>
            <TextInput
              className='h-7'
              onChange={v => {
                const existing = order.products.get(OrderUtil.orderSpecifierHash(item) || '');
                if (!existing) return;
                existing.quantity = parseFloat(v) || 0;
                setOrder(cloneDeep(order));
              }}
              value={item.quantity.toString()}
              type='number'
              trailing={
                <div className='text-gray-400'>
                  {UnitOfMeasureUtil.getSuffix(productMasterData.get(item.pmdId || '')?.unitOfMeasure) || ' '}
                </div>
              }
            />
          </div>
        );
      },
    },
  ];

  const onContainerSelect = (container?: Container) => {
    if (!container) return;

    const containerProducts = getAllProductsForContainer(container.id);
    for (const product of containerProducts) {
      const pmd = productMasterData.get(product.productMasterDataId);
      order.withOutboundProduct(product, pmd?.unitOfMeasure);
    }
    setOrder(new OrderInput(order));
  };

  return (
    <div className='mx-6 flex h-full flex-col gap-2 py-2'>
      <DropdownSearchSelect
        placeholder={t(TranslationKey.filterContainer).singular.label}
        items={containersWithQuantity}
        textBuilder={container => container.identifier}
        value={undefined}
        onSelect={onContainerSelect}
        itemBuilder={container => {
          return (
            <div className='flex flex-col gap-1'>
              <div className='flex gap-2'>
                <BoxIcon />
                <p className='text-sm'>{container.identifier}</p>
              </div>
            </div>
          );
        }}
      />
      <Table<ProductTransactionSpecifiers>
        modal
        uniqueIdentifier={item => OrderUtil.orderSpecifierHash(item)}
        onSelected={items => setSelected(new Set(items))}
        selectedValues={selected}
        headers={headers}
        items={orderProducts}
      />
      <div className='flex justify-between'>
        <div>
          {selected.size ? (
            <DeleteButton
              onClick={() => {
                setOrder(order.removeProducts(selected));
                setSelected(new Set());
              }}
            />
          ) : null}
        </div>
        <div className='flex gap-2'>
          {back && <Button text={t(TranslationKey.back).singular.label} onClick={back} />}
          {next && (
            <Button style={ColorStyleType.secondary} text={t(TranslationKey.next).singular.label} onClick={next} />
          )}
        </div>
      </div>
    </div>
  );
}
